import { last } from 'lib/array/arrayUtils'
import type { FlightSchedule } from 'tripPlanner/types/common'

/**
 * Attempt to sort according to the sequence of airports.
 * Time complexity is O(n^2) but the input array should always
 * be small so it doesn't matter.
 */
export function sortFlightSchedules(
  flights: Array<FlightSchedule>,
): Array<FlightSchedule> {
  if (flights.length < 2) {
    return flights
  }

  const arrivalAirports = new Set(flights.map((f) => f.arrivalAirportCode))
  const firstFlightIndex = flights.findIndex(
    (flight) => !arrivalAirports.has(flight.departureAirportCode),
  )
  if (firstFlightIndex === -1) {
    // Some kind of crazy circular flight route
    return flights
  }
  const unsorted = [...flights]
  const [first] = unsorted.splice(firstFlightIndex, 1)
  const sorted: Array<FlightSchedule> = [first]

  // Find flights after the first one we picked
  while (unsorted.length > 0) {
    const lastArrivalAirport = last(sorted).arrivalAirportCode
    const nextFlightIndex = unsorted.findIndex(
      (flight) => flight.departureAirportCode === lastArrivalAirport,
    )
    if (nextFlightIndex > -1) {
      const [next] = unsorted.splice(nextFlightIndex, 1)
      sorted.push(next)
    } else {
      break
    }
  }

  if (unsorted.length === 0) {
    // We got them all! :D
    return sorted
  } else {
    // Apparently the airports weren't sequential :(
    return flights
  }
}

export function addSpaceToFlightNumber(
  flightNo: string | undefined,
): string | undefined {
  if (!flightNo) return flightNo

  const matches = flightNo.match(/^([A-Z]{2})(\d+)$/)

  if (matches) {
    return `${matches[1]} ${matches[2]}`
  } else {
    return flightNo
  }
}
